/**
 * Шаг 2 wizard «Модели» (E07.2, ФТ-1.2).
 * Карточки по типу; мультивыбор с порядковыми номерами; клик по типоразмеру выбирает модель.
 * Визуал Model pick (Figma Potok / node 1:529).
 */

import { useEffect, useMemo, useState } from "react";
import type { PublicModelCard, PublicModelMetric } from "@potok/shared";
import type { PublicCatalogApi } from "../../api/catalog.api";
import { ApiRequestError } from "../../api/http";

export type ModelSelection = {
  modelId: string;
  preferredUnitId: string | null;
};

export type ModelsStepProps = {
  api: PublicCatalogApi;
  industryId: string | null;
  selected: readonly ModelSelection[];
  onChange: (selected: ModelSelection[]) => void;
  onBack: () => void;
  onContinue: () => void;
};

export function ModelsStep({
  api,
  industryId,
  selected,
  onChange,
  onBack,
  onContinue,
}: ModelsStepProps) {
  const [models, setModels] = useState<PublicModelCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .listModels(industryId)
      .then((rows) => {
        if (!cancelled) {
          setModels(rows);
        }
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(describeError(caught));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, industryId]);

  const groups = useMemo(() => groupModels(models), [models]);
  const orderById = useMemo(() => {
    const map = new Map<string, number>();
    selected.forEach((item, index) => map.set(item.modelId, index + 1));
    return map;
  }, [selected]);

  function toggleModel(modelId: string, preferredUnitId: string | null = null): void {
    const existing = selected.find((item) => item.modelId === modelId);
    if (existing) {
      if (preferredUnitId !== null && existing.preferredUnitId !== preferredUnitId) {
        onChange(
          selected.map((item) => (item.modelId === modelId ? { ...item, preferredUnitId } : item)),
        );
        return;
      }
      onChange(selected.filter((item) => item.modelId !== modelId));
      return;
    }
    onChange([...selected, { modelId, preferredUnitId }]);
  }

  return (
    <section className="wizard-step wizard-step--models" aria-labelledby="models-step-title">
      <div className="wizard-step__heading">
        <div className="wizard-step__heading-text">
          <h2 id="models-step-title" className="wizard-step__title">
            Выберите модель
          </h2>
          <p className="wizard-step__hint">
            Шаг 2 из 5 — Показаны модели для выбранной отрасли
          </p>
        </div>
        <button type="button" className="wizard-step__back" onClick={onBack}>
          ← Назад
        </button>
      </div>

      {error ? (
        <p className="wizard-step__status wizard-step__status--error" role="alert">
          {error}
        </p>
      ) : null}
      {loading ? <p className="wizard-step__hint">Загрузка моделей…</p> : null}

      {!loading && !error
        ? groups.map((group) => (
            <div key={group.id} className="model-group">
              <h3 className="model-group__title">{group.name}</h3>
              <ul className="model-grid">
                {group.models.map((model) => {
                  const order = orderById.get(model.id);
                  const selection = selected.find((item) => item.modelId === model.id);
                  const metrics = cardMetrics(model.metrics);
                  return (
                    <li key={model.id}>
                      <article
                        className={
                          order === undefined ? "model-card" : "model-card model-card--selected"
                        }
                      >
                        <button
                          type="button"
                          className="model-card__select"
                          aria-pressed={order !== undefined}
                          onClick={() => toggleModel(model.id)}
                        >
                          {order !== undefined ? (
                            <span className="model-card__order" aria-label={`Выбор ${order}`}>
                              {order}
                            </span>
                          ) : null}
                          <span className="model-card__name">{model.name}</span>
                        </button>

                        <p className="model-card__desc">{modelCardDescription(model)}</p>

                        {metrics.length > 0 ? (
                          <ul className="model-card__metrics">
                            {metrics.map((metric) => {
                              const parts = metricParts(metric);
                              return (
                                <li key={metric.attributeId} className="model-card__metric">
                                  <span className="model-card__metric-label">{parts.label}</span>
                                  <span className="model-card__metric-value">{parts.value}</span>
                                </li>
                              );
                            })}
                          </ul>
                        ) : null}

                        {model.units.length > 0 ? (
                          <ul
                            className="model-card__units"
                            aria-label={`Типоразмеры ${model.name}`}
                          >
                            {model.units.map((unit) => (
                              <li key={unit.id}>
                                <button
                                  type="button"
                                  className={
                                    selection?.preferredUnitId === unit.id
                                      ? "model-card__unit model-card__unit--active"
                                      : "model-card__unit"
                                  }
                                  onClick={() => toggleModel(model.id, unit.id)}
                                >
                                  {unit.code}
                                </button>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </article>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        : null}

      {!loading && !error && models.length === 0 ? (
        <p className="wizard-step__hint">Нет опубликованных моделей для выбранной отрасли</p>
      ) : null}

      <div className="wizard-step__actions wizard-step__actions--end">
        <button type="button" className="wizard-step__primary" onClick={onContinue}>
          {selected.length === 0 ? "Пропустить" : "Далее"}
        </button>
      </div>
    </section>
  );
}

function groupModels(
  models: PublicModelCard[],
): { id: string; name: string; models: PublicModelCard[] }[] {
  const map = new Map<
    string,
    { id: string; name: string; sortOrder: number; models: PublicModelCard[] }
  >();
  for (const model of models) {
    const group = map.get(model.groupId) ?? {
      id: model.groupId,
      name: model.groupName,
      sortOrder: model.groupSortOrder,
      models: [],
    };
    group.models.push(model);
    map.set(model.groupId, group);
  }
  return [...map.values()]
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, "ru"))
    .map(({ id, name, models: items }) => ({ id, name, models: items }));
}

/** Предпочитает V / Q / L (скорость, производительность, длина), иначе первые три метрики. */
function cardMetrics(metrics: PublicModelMetric[]): PublicModelMetric[] {
  const byFormula = new Map(
    metrics
      .filter((item) => item.formulaName !== null)
      .map((item) => [item.formulaName!.toUpperCase(), item]),
  );
  const preferred = ["V", "Q", "L"]
    .map((code) => byFormula.get(code))
    .filter((item): item is PublicModelMetric => item !== undefined);
  if (preferred.length > 0) {
    return preferred;
  }
  return metrics.slice(0, 3);
}

function metricParts(metric: PublicModelMetric): { label: string; value: string } {
  const label = metricLabel(metric);
  const unit = metric.unitName ? ` ${metric.unitName}` : "";
  if (metric.min !== null && metric.max !== null && metric.min !== metric.max) {
    return { label, value: `${metric.min}–${metric.max}${unit}` };
  }
  if (metric.formulaName?.toUpperCase() === "Q" && metric.max !== null) {
    return { label, value: `до ${metric.max}${unit}` };
  }
  const value = metric.max ?? metric.min;
  return { label, value: value === null ? "—" : `${value}${unit}` };
}

function metricLabel(metric: PublicModelMetric): string {
  switch (metric.formulaName?.toUpperCase()) {
    case "V":
      return "Скорость";
    case "Q":
      return "Производит.";
    case "L":
      return "Длина/высота";
    default:
      return metric.name;
  }
}

/** Краткое описание под названием: отрасли или тип группы (API без description). */
function modelCardDescription(model: PublicModelCard): string {
  if (model.industries.length > 0) {
    return model.industries.map((industry) => industry.name).join(", ");
  }
  return `Модель группы «${model.groupName}»`;
}

function describeError(error: unknown): string {
  if (error instanceof ApiRequestError) {
    return error.message;
  }
  return "Не удалось загрузить модели";
}
